// src/components/audio/audioNotes.js
//
// Pure data layer for audio reviewer notes and submission dispositions.
// No React, no UI — just the canonical schema, tag/disposition vocabulary,
// and helpers that the rest of the app uses.
//
// SCHEMA (the document persisted to ConfigurationCRUD under 'audio_notes')
// {
//   notes: [
//     {
//       id:                "n_3f9a2b",       // generated, see makeNoteId()
//       audio_path:        "C:\\...\\AA_xxx.m4a",
//       audio_column:      "audio_audit",     // which column produced this file
//       submission_id:     "uuid:e909...",    // links back to submission
//       timestamp_seconds: 754,               // playback position
//       text:              "Skipped probing on gender",
//       tag:               "probing",         // see TAGS below
//       created_at:        "2026-05-03T11:23:45Z",
//       updated_at:        "2026-05-03T11:23:45Z",
//       author:            "Mwiinga Choobe",  // userInfo.name preferred
//     },
//     ...
//   ],
//   dispositions: {
//     "uuid:e909...|audio_audit":      "good",
//     "uuid:e909...|bundle_tasks_3":   "concern",
//     ...
//   },
//   updated_at: "..."
// }
//
// Why notes are flat array, not keyed by file:
//   - Easier to filter for export ("all probing notes across enumerators")
//   - Easier to migrate later (add fields, etc.)
//   - getNotesForPath() is O(n) but n is bounded — even a project with
//     5000 notes filters in <2ms in modern browsers

// ── Tag vocabulary (closed set, intentional) ───────────────────────────────
// Free-form tags devolve into "probe", "probing", "Probing", "Probe Depth"
// within a week, making aggregation useless. This locked list keeps notes
// grouping consistent. Add tags here when a real new category emerges.
export const TAGS = [
  { id: 'probing',       label: 'Probing',       color: '#D97706', bgAlpha: 0x15, description: 'Insufficient probing on a question' },
  { id: 'fabrication',   label: 'Fabrication',   color: '#DC2626', bgAlpha: 0x15, description: 'Suspected fabricated response' },
  { id: 'audio_quality', label: 'Audio quality', color: '#6B7280', bgAlpha: 0x18, description: 'Recording quality issue' },
  { id: 'language',      label: 'Language',      color: '#2563EB', bgAlpha: 0x15, description: 'Language barrier or mistranslation' },
  { id: 'off_topic',     label: 'Off-topic',     color: '#7C3AED', bgAlpha: 0x15, description: 'Off-topic conversation' },
  { id: 'other',         label: 'Other',         color: '#0F766E', bgAlpha: 0x15, description: 'Something else worth noting' },
];

export const TAGS_BY_ID = Object.fromEntries(TAGS.map(t => [t.id, t]));

export const getTag = (tagId) => TAGS_BY_ID[tagId] || TAGS_BY_ID.other;

// ── Disposition vocabulary (closed set) ────────────────────────────────────
// Three real states + one implicit "unset" (no entry in the dispositions map).
export const DISPOSITIONS = [
  { id: 'good',    label: 'Good',    color: '#059669', description: 'Approved — no issues found' },
  { id: 'concern', label: 'Concern', color: '#D97706', description: 'Worth coaching, but data is usable' },
  { id: 'reject',  label: 'Reject',  color: '#DC2626', description: "Don't include in final dataset" },
];

export const DISPOSITIONS_BY_ID = Object.fromEntries(DISPOSITIONS.map(d => [d.id, d]));

export const getDisposition = (dispId) => DISPOSITIONS_BY_ID[dispId] || null;

// ── ID + timestamp helpers ─────────────────────────────────────────────────
export const makeNoteId = () => {
  // Short random ID — collision-resistant enough for per-project notes
  // (probability of collision over 100k notes ≈ 1e-12).
  // Format: n_<base36 timestamp>_<base36 random>
  const t = Date.now().toString(36);
  const r = Math.floor(Math.random() * 0x7fffffff).toString(36);
  return `n_${t}${r}`;
};

export const nowISO = () => new Date().toISOString();

// Compose the disposition map key. The same submission can have different
// dispositions for different audio columns (audio_audit might be 'good'
// while bundle_tasks_3 is 'concern').
export const dispositionKey = (submissionId, audioColumn) =>
  `${submissionId || '?'}|${audioColumn || '?'}`;

// ── Empty document (used when no notes/dispositions saved yet) ────────────
export const emptyDocument = () => ({
  notes: [],
  dispositions: {},
  updated_at: null,
});

// ── Selectors over a loaded document ───────────────────────────────────────

/**
 * Notes for a specific audio file, sorted by timestamp ascending.
 * Path comparison is normalized — backslash/forward slash and trailing
 * whitespace differences don't break matching.
 */
export const getNotesForPath = (doc, audioPath) => {
  if (!doc?.notes || !audioPath) return [];
  const target = normalizePath(audioPath);
  return doc.notes
    .filter(n => normalizePath(n.audio_path) === target)
    .sort((a, b) => (a.timestamp_seconds ?? 0) - (b.timestamp_seconds ?? 0));
};

/**
 * Note count for a path (cheap O(n) scan; for many files in a playlist,
 * caller should build a Map once rather than calling this repeatedly).
 */
export const countNotesForPath = (doc, audioPath) => {
  if (!doc?.notes || !audioPath) return 0;
  const target = normalizePath(audioPath);
  let count = 0;
  for (const n of doc.notes) {
    if (normalizePath(n.audio_path) === target) count++;
  }
  return count;
};

/**
 * Build a Map<normalizedPath, count> in one pass — preferred when rendering
 * a long playlist of submissions.
 */
export const buildNoteCountMap = (doc) => {
  const m = new Map();
  if (!doc?.notes) return m;
  for (const n of doc.notes) {
    const key = normalizePath(n.audio_path);
    m.set(key, (m.get(key) || 0) + 1);
  }
  return m;
};

/**
 * Disposition for a submission + audio column (returns id string or null).
 */
export const getDispositionFor = (doc, submissionId, audioColumn) => {
  if (!doc?.dispositions) return null;
  return doc.dispositions[dispositionKey(submissionId, audioColumn)] || null;
};

// ── Mutations (return a new document — pure functions, no in-place edits) ──

/**
 * Add a new note. Returns a fresh document (immutable update).
 * Required: audio_path, audio_column, submission_id, timestamp_seconds, text, tag.
 */
export const addNote = (doc, partial) => {
  const note = {
    id: makeNoteId(),
    audio_path:        partial.audio_path,
    audio_column:      partial.audio_column,
    submission_id:     partial.submission_id,
    timestamp_seconds: Math.max(0, Math.floor(partial.timestamp_seconds || 0)),
    text:              String(partial.text || '').trim(),
    tag:               partial.tag || 'other',
    created_at:        nowISO(),
    updated_at:        nowISO(),
    author:            partial.author || null,
  };
  return {
    ...doc,
    notes: [...(doc?.notes || []), note],
    updated_at: nowISO(),
  };
};

/**
 * Update an existing note. `patch` can include any of: text, tag,
 * timestamp_seconds. Other fields are immutable post-creation.
 */
export const updateNote = (doc, noteId, patch) => {
  if (!doc?.notes) return doc;
  return {
    ...doc,
    notes: doc.notes.map(n => {
      if (n.id !== noteId) return n;
      const next = { ...n };
      if (patch.text !== undefined)              next.text              = String(patch.text).trim();
      if (patch.tag !== undefined)               next.tag               = patch.tag;
      if (patch.timestamp_seconds !== undefined) next.timestamp_seconds = Math.max(0, Math.floor(patch.timestamp_seconds));
      next.updated_at = nowISO();
      return next;
    }),
    updated_at: nowISO(),
  };
};

/**
 * Delete a note by ID.
 */
export const deleteNote = (doc, noteId) => {
  if (!doc?.notes) return doc;
  return {
    ...doc,
    notes: doc.notes.filter(n => n.id !== noteId),
    updated_at: nowISO(),
  };
};

/**
 * Set or clear a disposition. Pass dispId === null to clear.
 */
export const setDisposition = (doc, submissionId, audioColumn, dispId) => {
  const key = dispositionKey(submissionId, audioColumn);
  const next = { ...(doc?.dispositions || {}) };
  if (dispId == null) {
    delete next[key];
  } else {
    next[key] = dispId;
  }
  return {
    ...doc,
    dispositions: next,
    updated_at: nowISO(),
  };
};

// ── Export helpers (used by Phase 2 CSV export) ────────────────────────────

/**
 * Flatten notes into rows with submission/enumerator/firm context for CSV.
 * Caller supplies a lookup function `submissionInfoFor(submissionId)`
 * returning { enumerator, firm, submission_date } or {} if unknown.
 */
export const notesToRows = (doc, submissionInfoFor) => {
  if (!doc?.notes) return [];
  return doc.notes.map(n => {
    const ctx = submissionInfoFor ? (submissionInfoFor(n.submission_id) || {}) : {};
    return {
      timestamp:      formatTimestamp(n.timestamp_seconds),
      timestamp_sec:  n.timestamp_seconds,
      tag:            n.tag,
      text:           n.text,
      audio_column:   n.audio_column,
      audio_path:     n.audio_path,
      enumerator:     ctx.enumerator || '',
      firm:           ctx.firm || '',
      submission_id:  n.submission_id,
      submission_date: ctx.submission_date || '',
      created_at:     n.created_at,
      updated_at:     n.updated_at,
      author:         n.author || '',
    };
  });
};

// ── Internal helpers ───────────────────────────────────────────────────────

const normalizePath = (p) => {
  if (!p) return '';
  return String(p).replace(/\\/g, '/').replace(/\/+$/, '').trim().toLowerCase();
};

const formatTimestamp = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export { formatTimestamp };
